// Build the compact, manual-only output JSON consumed by Submit (spec §5.5).
// Pure helper — no IndexedDB, no DOM. The edits slice (subtask 3) enforces
// the `out_of_frame -> xy = [-1, -1]` invariant on write; here we only pass
// it through.
//
// Stable frame ordering: emit frame keys numerically ascending so two
// equivalent edits produce byte-identical JSON (helps diff-by-eye during
// manual QA and any future content-hash signing). Frame keys are decimal
// strings ("0", "47"), never zero-padded.

import type { EditedKeypoint, EditMap, KeypointName, OutputJSON } from '../types';

export interface CompactOutputHeader {
  video: string;
  image_size: [number, number];
  fps: number;
  num_frames: number;
}

export function buildCompactOutput(
  _side: 'front_left' | 'front_right' | 'left' | 'right',
  header: CompactOutputHeader,
  edits: EditMap
): OutputJSON {
  const out: EditMap = {};
  const frameKeys = Object.keys(edits).sort((a, b) => Number(a) - Number(b));
  for (const frameKey of frameKeys) {
    const frameEdits = edits[Number(frameKey)];
    if (!frameEdits) continue;
    const names = Object.keys(frameEdits) as KeypointName[];
    if (names.length === 0) continue;
    const frameOut: Partial<Record<KeypointName, EditedKeypoint>> = {};
    for (const name of names) {
      const edit = frameEdits[name];
      if (edit && !edit.seeded) frameOut[name] = edit;
    }
    if (Object.keys(frameOut).length === 0) continue;
    out[Number(frameKey)] = frameOut;
  }
  return { ...header, schema_version: 1, edits: out };
}
